import React, { Suspense, lazy, Component } from 'react'
import { Link } from 'react-router-dom'
import Icon from './icons'

// Ported from the source project's src/components/site/Hero.tsx, framer-motion entrance
// animation dropped (this repo has no framer-motion dependency; Nav/Footer set the same
// precedent in Task 3 - CSS-only presentation, no animation library).
//
// Lazy so three.js lands in its own chunk. CRA emits one main.js for every route, so a
// static import would ship ~170 kB gzip of 3D to every labadmin and doctor session on
// every page load, for a landing page those users never open.
const Hero3D = lazy(() => import('./Hero3D'));

// Catches BOTH a failed chunk fetch (a lazy-import rejection throws from the lazy() call
// itself, outside Hero3D.jsx entirely, since the module never loaded) AND any render-time
// throw from the loaded component (e.g. WebGL context creation failing). This must wrap
// <Suspense>, not sit inside it - Suspense only covers the pending "still loading" state,
// not either failure mode, and this app has no top-level boundary (src/index.js renders
// <App/> bare). Without this, a bad chunk fetch - a CDN hiccup, a partial deploy, a stale
// index.html referencing a chunk that no longer exists - would blank the entire page, not
// just the hero.
class Hero3DBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(error) {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('Hero3D failed to load/render, falling back to the static hero:', error);
        }
    }
    render() {
        return this.state.hasError ? null : this.props.children;
    }
}

function badgeClass(status) {
    const s = status.toLowerCase();
    if (s.includes('deliver') || s.includes('done')) return 'badge-done';
    if (s.includes('scan') || s.includes('pend')) return 'badge-pending';
    return 'badge-progress';
}
function dotClass(status) {
    const s = status.toLowerCase();
    if (s.includes('deliver') || s.includes('done')) return 'case-dot done';
    if (s.includes('scan') || s.includes('pend')) return 'case-dot pending';
    return 'case-dot';
}

const Hero = ({ section, heroCases, heroStats, three }) => {
    const c = section.content ?? {};
    const titleLines = c.titleLines ?? [section.title ?? ''];
    const emphasisFrom = c.emphasisFrom ?? titleLines.length;
    const primaryCta = c.primaryCta ?? 'Submit a case';
    const secondaryCta = c.secondaryCta ?? 'Request fee schedule';
    const floatTitle = c.floatTagTitle ?? 'Case #4821';
    const floatSub = c.floatTagSub ?? 'Ready for delivery';

    return (
        <section className="hero-section" id="top">
            <div className="hero">
                <div className="hero-content">
                    <div className="hero-eyebrow">{section.eyebrow}</div>
                    <h1>
                        {titleLines.map((line, i) => (
                            <span key={i}>
                                {i >= emphasisFrom ? <em>{line}</em> : line}
                                {i < titleLines.length - 1 && <br />}
                            </span>
                        ))}
                    </h1>
                    <p className="hero-desc">{section.subtitle}</p>
                    <div className="hero-actions">
                        <Link className="btn-lg" to="/contant">
                            {primaryCta}
                            <Icon name="arrow" />
                        </Link>
                        <Link className="btn-lg-ghost" to="/contant">{secondaryCta}</Link>
                    </div>
                    <div className="hero-stats">
                        {heroStats.map((st) => (
                            <div key={st.order}>
                                <div className="stat-val">{st.value}</div>
                                <div className="stat-label">{st.label}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="hero-visual">
                    <div className="hero-3d">
                        {/* Fallback is nothing: the hero's own gradient renders behind the canvas
                            anyway, so a slow chunk shows a complete hero rather than a gap. The
                            boundary wraps Suspense (not the other way around) so it also catches
                            a failed chunk fetch, not just a render-time throw once loaded. */}
                        <Hero3DBoundary>
                            <Suspense fallback={null}><Hero3D cfg={three}/></Suspense>
                        </Hero3DBoundary>
                    </div>

                    <div className="float-tag">
                        <div className="float-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                                <circle cx="12" cy="9" r="2.5" />
                            </svg>
                        </div>
                        <div>
                            <div className="float-val">{floatTitle}</div>
                            <div className="float-sub">{floatSub}</div>
                        </div>
                    </div>

                    <div className="hero-card">
                        <div className="hc-top">
                            <div className="hc-dots"><span /><span /><span /></div>
                            <div className="hc-title">Case management portal</div>
                            <div />
                        </div>
                        <div className="hc-body">
                            {heroCases.map((row) => (
                                <div className="case-row" key={row.order}>
                                    <div className="case-left">
                                        <div className={dotClass(row.status)} />
                                        <div>
                                            <div className="case-name">{row.name}</div>
                                            <div className="case-type">{row.type}</div>
                                        </div>
                                    </div>
                                    <span className={`case-badge ${badgeClass(row.status)}`}>{row.status}</span>
                                </div>
                            ))}
                        </div>
                        <div className="hc-footer">
                            <span className="hc-footer-text">{heroCases.length} active cases · Updated just now</span>
                            <span className="hc-footer-btn">View all →</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
